import { useCallback, useState } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import { AgreementSelect } from '@retail/purchase-agreements/components';
import { ItemAgreementPrices, PurchaseAgreement } from '@retail/purchase-agreements/types';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useConditionActions } from '@retail/calculus/context';
import { SelectItemAgreements } from '@retail/purchase-agreements/containers';
import { EditConditionDialog } from '../../ConditionActionsBase';

interface Props {
  open: boolean;
  onClose: () => void;
  assortmentId: number;
  agreements: PurchaseAgreement[];
  editCondition: ConditionsNew;
}

function EditAgreementConditionDialog({
  open,
  assortmentId,
  agreements,
  editCondition,
  onClose,
}: Props) {
  const t = useAppTFunction();

  const { createOrUpdateCondition, isLoadingPostChanges } = useConditionActions();

  const ownAgreementId = editCondition.purchaseCondition?.isInherited
    ? undefined
    : editCondition.purchaseCondition?.agreementId;

  const [selectedAgreement, selectAgreement] = useState<number | undefined>(ownAgreementId);
  const [selectedItemAgreement, selectItemAgreement] = useState<ItemAgreementPrices | undefined>(
    undefined
  );

  const submitAgreements = useCallback(() => {
    createOrUpdateCondition({
      content: {
        purchase: {
          agreementId: selectedItemAgreement?.agreementId ?? selectedAgreement,
          priceType: selectedItemAgreement?.priceType,
          packageType: selectedItemAgreement?.packageType,
        },
      },
      bindingValue: editCondition.getBindingLevel(),
      validFrom: new Date(),
    }).then(onClose);
  }, [
    createOrUpdateCondition,
    selectedItemAgreement?.agreementId,
    selectedItemAgreement?.priceType,
    selectedItemAgreement?.packageType,
    selectedAgreement,
    editCondition,
    onClose,
  ]);

  return (
    <EditConditionDialog
      open={open}
      title={t('purchaseAgreements.agreement.actions.editConditionFor', {
        name: editCondition.bindingTargetName,
      })}
      dialogContent={
        editCondition.bindingTargetItem ? (
          <SelectItemAgreements
            supplierItemId={editCondition.bindingTargetItem.supplierItemId}
            assortmentId={assortmentId}
            selectAgreement={selectItemAgreement}
          />
        ) : (
          <AgreementSelect
            agreements={agreements}
            selectedAgreement={selectedAgreement}
            activeConditions={[editCondition]}
            selectAgreement={selectAgreement}
          />
        )
      }
      isLoading={isLoadingPostChanges}
      isDisabled={
        !selectedItemAgreement && (!selectedAgreement || selectedAgreement === ownAgreementId)
      }
      onClose={onClose}
      onSubmit={submitAgreements}
    />
  );
}

export default withDialogSuspenseErrorBoundary(EditAgreementConditionDialog);
